/**
 * Route Reviews Component
 * Displays a list of reviews for a route.
 */

import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { Star, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/authContext";
import { useI18n } from "@/lib/i18n";
import { RouteRatingModal } from "@/components/RouteRatingModal";

interface RouteReviewsProps {
    routeId: string;
    routeName: string;
}

export function RouteReviews({ routeId, routeName }: RouteReviewsProps) {
    const { t } = useI18n();
    const { user } = useAuth();
    const [ratingModalOpen, setRatingModalOpen] = useState(false);
    const [existingReview, setExistingReview] = useState<any>(null);

    // Fetch reviews
    const { data: reviewsData, isLoading } = useQuery({
        queryKey: ["route-reviews", routeId],
        queryFn: async () => {
            const res = await fetch(`/api/routes/${routeId}/reviews`);
            if (!res.ok) throw new Error("Failed to fetch reviews");
            return res.json();
        },
    });

    // Fetch user's review if logged in
    const { data: userReviewData, refetch: refetchUserReview } = useQuery({
        queryKey: ["route-user-review", routeId, user?.id],
        queryFn: async () => {
            if (!user) return null;
            const { apiRequest } = await import("@/lib/queryClient");
            const res = await apiRequest("GET", `/api/routes/${routeId}/reviews/my-review`);
            return res.json();
        },
        enabled: !!user,
    });

    useEffect(() => {
        if (userReviewData?.review) {
            setExistingReview(userReviewData.review);
        } else {
            setExistingReview(null);
        }
    }, [userReviewData]);

    const reviews = reviewsData?.reviews || [];

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">{t("shops.reviews")} ({reviews.length})</h2>
                <Button variant="outline" onClick={() => setRatingModalOpen(true)}>
                    {existingReview ? t("shopRating.editReview") : t("shopRating.writeReview")}
                </Button>
            </div>

            {isLoading ? (
                <div className="text-center py-8 text-muted-foreground">{t("common.loading")}</div>
            ) : reviews.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground bg-muted/5 rounded-lg border border-dashed">
                    {t("shops.noReviewsYet")}
                </div>
            ) : (
                <div className="space-y-4">
                    {reviews.map((review: any) => (
                        <div key={review.id} className="p-4 border rounded-lg bg-card">
                            <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <Avatar className="h-8 w-8">
                                        <AvatarImage src={review.user.avatarUrl} />
                                        <AvatarFallback><User className="h-4 w-4" /></AvatarFallback>
                                    </Avatar>
                                    <div>
                                        <div className="font-medium text-sm">{review.user.username || t("common.anonymous")}</div>
                                        <div className="text-xs text-muted-foreground">
                                            {format(new Date(review.created_at), "d MMMM yyyy", { locale: nl })}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <Star
                                            key={star}
                                            className={`h-4 w-4 ${star <= review.rating ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground fill-muted-foreground/20"}`}
                                        />
                                    ))}
                                </div>
                            </div>

                            {review.comment && (
                                <p className="text-sm text-foreground/90 mt-2 whitespace-pre-line">{review.comment}</p>
                            )}

                            {review.photoUrls && review.photoUrls.length > 0 && (
                                <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                                    {review.photoUrls.map((url: string, idx: number) => (
                                        <img
                                            key={idx}
                                            src={url}
                                            alt="Review photo"
                                            className="h-24 w-24 object-cover rounded-md border"
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <RouteRatingModal
                open={ratingModalOpen}
                onOpenChange={setRatingModalOpen}
                routeId={routeId}
                routeName={routeName}
                existingReview={existingReview}
                onSuccess={() => {
                    refetchUserReview();
                    // refetch reviews list
                    import("@/lib/queryClient").then(({ queryClient }) => {
                        queryClient.invalidateQueries({ queryKey: ["route-reviews", routeId] });
                        // Also invalidate route details to update rating average
                        queryClient.invalidateQueries({ queryKey: ["route", routeId] });
                    });
                }}
            />
        </div>
    );
}
